import { useState, useEffect } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogClose,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { authorizedFetch } from "@/lib/api"
import { computeTrajectoryCode, computeTrajectoryName, ROUTE_CODE_MAX } from "@cdc-sccr/shared"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

export interface QuickRouteTarget {
  originCityId: string
  targetCityId: string
  originCityName: string
  targetCityName: string
}

interface Props {
  target: QuickRouteTarget | null
  onClose: () => void
}

export function QuickRouteDialog({ target, onClose }: Props) {
  const qc = useQueryClient()
  const [km,   setKm]   = useState("")
  const [code, setCode] = useState("")
  const [name, setName] = useState("")
  const [error, setError] = useState<string | null>(null)

  // Reset fields whenever a new target is opened
  useEffect(() => {
    if (target) {
      setKm("")
      setCode(computeTrajectoryCode([{ name: target.originCityName }, { name: target.targetCityName }]))
      setName(computeTrajectoryName([{ name: target.originCityName }, { name: target.targetCityName }]))
      setError(null)
    }
  }, [target])

  const createMut = useMutation({
    mutationFn: async (data: { code: string; name: string; originCityId: string; targetCityId: string; km: number }) => {
      const res = await authorizedFetch(`${API_URL}/api/geo/routes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const b = await res.json() as { error?: string }
        throw new Error(b.error ?? "Error al crear ruta")
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      onClose()
    },
    onError: (e: Error) => setError(e.message),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const kmNum = km.trim() === "" ? null : Number(km)
    if (!km.trim()) { setError("El kilometraje es requerido"); return }
    if (!Number.isFinite(kmNum) || kmNum! <= 0) { setError("El kilometraje debe ser un número mayor a 0"); return }
    if (!code.trim()) { setError("El código es requerido"); return }
    if (!name.trim()) { setError("El nombre es requerido"); return }
    if (!target) return
    createMut.mutate({
      code: code.trim().toUpperCase(),
      name: name.trim(),
      originCityId: target.originCityId,
      targetCityId: target.targetCityId,
      km: Math.trunc(kmNum!),
    })
  }

  return (
    <Dialog open={target !== null} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Crear ruta</DialogTitle>
          <DialogDescription>
            Ingresá el kilometraje para registrar esta ruta. Nombre y código se generan automáticamente y pueden modificarse.
          </DialogDescription>
        </DialogHeader>
        {target && (
          <form id="quick-route-form" onSubmit={handleSubmit} className="flex flex-col gap-4 pt-2">
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2.5 text-sm">
              <span className="font-medium text-xs text-muted-foreground uppercase tracking-wide w-14 shrink-0">Origen</span>
              <span className="font-medium">{target.originCityName || "—"}</span>
            </div>
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2.5 text-sm">
              <span className="font-medium text-xs text-muted-foreground uppercase tracking-wide w-14 shrink-0">Destino</span>
              <span className="font-medium">{target.targetCityName || "—"}</span>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="qr-km" className="text-xs">Kilometraje <span className="text-destructive">*</span></Label>
              <Input
                id="qr-km"
                type="number"
                min={1}
                step={1}
                placeholder="Ej. 1850"
                value={km}
                onChange={(e) => setKm(e.target.value)}
                autoFocus
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="qr-code" className="text-xs">Código <span className="text-muted-foreground">(auto)</span></Label>
              <Input
                id="qr-code"
                maxLength={ROUTE_CODE_MAX}
                className="font-mono uppercase"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="qr-name" className="text-xs">Nombre <span className="text-muted-foreground">(auto)</span></Label>
              <Input
                id="qr-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            {error && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
            )}
          </form>
        )}
        <DialogFooter>
          <DialogClose>
            <Button type="button" variant="outline">Cancelar</Button>
          </DialogClose>
          <Button type="submit" form="quick-route-form" disabled={createMut.isPending}>
            {createMut.isPending ? "Guardando..." : "Guardar ruta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
